import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  ChecklistProfileService,
  ChecklistItemProfileService,
} from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import FormDialog from "common/Modal/Modal";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState } from "react";
import Loader from "layout/Loader";
import {
  ChecklistWithItemsRequest,
  GetChecklistByIdResponse,
} from "subModule/src/services/checklist/interface";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import DynamicSelect from "common/Select/Select";
import { Add, CloseOutlined, Remove } from "@mui/icons-material";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";

interface FormData {
  [key: string]: any;
}

const OPTION_TYPES = ["select", "checkbox", "radio"] as const;

const defaultItem = () => ({
  id: undefined as number | undefined,
  title: "",
  type: null as { value: string; label: string } | null,
  options: [] as string[],
  active: true,
  value: null as string | null,
});

const needsOptions = (typeValue?: string | null) =>
  !!typeValue &&
  OPTION_TYPES.includes(typeValue as (typeof OPTION_TYPES)[number]);

const normalizeOptions = (options: unknown, typeValue?: string | null) => {
  const list = Array.isArray(options)
    ? options.map((o) => (typeof o === "string" ? o : String(o ?? "")))
    : [];
  if (needsOptions(typeValue) && list.length === 0) return [""];
  return list;
};

interface ItemOptionsFieldArrayProps {
  itemIndex: number;
  control: Control<FormData>;
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  themeColors?: { primary?: string };
  onFormChange: () => void;
}

function ItemOptionsFieldArray({
  itemIndex,
  control,
  register,
  errors,
  themeColors,
  onFormChange,
}: ItemOptionsFieldArrayProps) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: `items.${itemIndex}.options`,
  });

  // Radio / checkbox / select always need at least one open option field
  useEffect(() => {
    if (fields.length === 0) {
      append("");
    }
  }, [fields.length, append]);

  return (
    <Grid container item xs={12} rowSpacing={1} columnSpacing={2}>
      {fields.map((field, optionIndex) => (
        <Grid item xs={12} md={3} key={field.id}>
          <TextField
            {...register(`items.${itemIndex}.options.${optionIndex}`, {
              validate: {
                required: (value) =>
                  (typeof value === "string" ? value.trim() : value) !== "" ||
                  t("This field is required."),
              },
              onChange: () => onFormChange(),
            })}
            label={`${t("option")} ${optionIndex + 1} *`}
            InputLabelProps={{ shrink: true }}
            placeholder={`${t("option")} ${optionIndex + 1}`}
            title={`${t("option")} ${optionIndex + 1}`}
            fullWidth
            error={!!(errors as any).items?.[itemIndex]?.options?.[optionIndex]}
            helperText={
              (errors as any).items?.[itemIndex]?.options?.[optionIndex]
                ?.message as string
            }
            InputProps={{
              endAdornment:
                optionIndex !== 0 ? (
                  <InputAdornment
                    position="end"
                    onClick={() => {
                      remove(optionIndex);
                      onFormChange();
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <CloseOutlined />
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
      ))}
      <Grid item xs={12} md={3} className="text-left">
        <Button
          onClick={() => {
            append("");
            onFormChange();
          }}
          className="mt-[30px] font-Saira font-semibold italic text-[12px] border-0 p-0 bg-transparent capitalize"
          style={{ color: themeColors?.primary }}
        >
          {t("addAnotherOption")}
        </Button>
      </Grid>
    </Grid>
  );
}

interface ChecklistItemsFieldArrayProps {
  control: Control<FormData>;
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  types: { value: string; label: string }[];
  themeColors?: { primary?: string };
  onFormChange: () => void;
  setIsBtnEnabled: (v: boolean) => void;
  IsBtnEnabled: boolean;
  isEditMode: boolean;
}

function ChecklistItemsFieldArray({
  control,
  register,
  errors,
  watch,
  setValue,
  types,
  themeColors,
  onFormChange,
  setIsBtnEnabled,
  IsBtnEnabled,
  isEditMode,
}: ChecklistItemsFieldArrayProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });
  const [deletingItemIndex, setDeletingItemIndex] = useState<number | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<{
    index: number;
    id?: number;
  } | null>(null);

  const handleDeleteItem = (itemIndex: number) => {
    if (fields.length <= 1 || deletingItemIndex !== null) return;
    const itemId = watch(`items.${itemIndex}.id`);
    // Create: remove locally, no confirmation modal.
    if (!isEditMode) {
      remove(itemIndex);
      onFormChange();
      return;
    }
    // Update: confirm, then delete via API when the item already exists.
    setDeleteTarget({
      index: itemIndex,
      id: itemId ? Number(itemId) : undefined,
    });
  };

  const confirmDeleteItem = () => {
    if (!deleteTarget || deletingItemIndex !== null) return;
    const { index: itemIndex, id: itemId } = deleteTarget;
    setDeleteTarget(null);

    if (isEditMode && itemId) {
      setDeletingItemIndex(itemIndex);
      ChecklistItemProfileService.deleteChecklistItem(itemId)
        .then((response: any) => {
          if (response?.success) {
            remove(itemIndex);
            onFormChange();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("ChecklistItem") +
                  " " +
                  t("deleted") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(
              <AlertMessages
                text={response?.message || t("Something went wrong")}
              />,
              { variant: "error" },
            );
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || t("Something went wrong")
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setDeletingItemIndex(null));
      return;
    }
    remove(itemIndex);
    onFormChange();
  };

  return (
    <>
      <Grid item xs={12}>
        <Typography
          className="font-Saira font-[700] text-[15px] text-left"
          style={{ color: themeColors?.primary }}
        >
          {t("checklistItems")} *
        </Typography>
        {(errors as any).items?.message ||
        (errors as any).items?.root?.message ? (
          <Typography className="text-[11px] text-[#d32f2f] mt-1">
            {
              ((errors as any).items?.message ||
                (errors as any).items?.root?.message) as string
            }
          </Typography>
        ) : null}
      </Grid>

      {fields.map((field, itemIndex) => {
        const typeValue = watch(`items.${itemIndex}.type`)?.value;
        return (
          <Grid
            container
            item
            xs={12}
            key={field.id}
            spacing={2}
            className="ms-0 mt-1 mb-2 border border-[#E5E7EB] rounded-[8px] p-3"
          >
            <Grid item xs={12} md={3}>
              <TextField
                {...register(`items.${itemIndex}.title`, {
                  validate: {
                    required: (value) =>
                      (typeof value === "string" ? value.trim() : value) !==
                        "" || t("This field is required."),
                  },
                  onChange: () => onFormChange(),
                })}
                label={t("title") + " *"}
                placeholder={t("title")}
                title={t("title")}
                fullWidth
                error={!!(errors as any).items?.[itemIndex]?.title}
                helperText={
                  (errors as any).items?.[itemIndex]?.title?.message as string
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3} style={{ marginTop: "2px" }}>
              <Controller
                name={`items.${itemIndex}.type`}
                control={control}
                defaultValue={null}
                rules={{ required: t("This field is required.") }}
                render={({ field: typeField }) => (
                  <DynamicSelect
                    label={t("type") + " *"}
                    {...typeField}
                    options={types}
                    isSearchable
                    onChange={(selectedOption: any) => {
                      typeField.onChange(selectedOption);
                      setValue(
                        `items.${itemIndex}.options`,
                        needsOptions(selectedOption?.value) ? [""] : [],
                        { shouldDirty: true, shouldValidate: true },
                      );
                      onFormChange();
                      if (!IsBtnEnabled) setIsBtnEnabled(true);
                    }}
                    error={
                      (errors as any).items?.[itemIndex]?.type
                        ?.message as string
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" alignItems="end" gap={1}>
              {fields.length > 1 ? (
                <IconButton
                  onClick={() => handleDeleteItem(itemIndex)}
                  color="error"
                  disabled={deletingItemIndex === itemIndex}
                  className="bg-[#E1E8E3] p-[5px] rounded-[8px] mt-[8px]"
                  sx={{ width: 34, height: 34 }}
                >
                  <Remove sx={{ fontSize: 19 }} />
                </IconButton>
              ) : null}
              {itemIndex === fields.length - 1 && (
                <IconButton
                  onClick={() => {
                    append(defaultItem());
                    onFormChange();
                  }}
                  className="bg-[#E1E8E3] p-[5px] rounded-[8px] mt-[8px]"
                  sx={{ width: 34, height: 34 }}
                >
                  <Add sx={{ fontSize: 19, color: themeColors?.primary }} />
                </IconButton>
              )}
            </Grid>
            {needsOptions(typeValue) ? (
              <ItemOptionsFieldArray
                key={`${field.id}-${typeValue}`}
                itemIndex={itemIndex}
                control={control}
                register={register}
                errors={errors}
                themeColors={themeColors}
                onFormChange={onFormChange}
              />
            ) : null}
          </Grid>
        );
      })}

      {fields.length === 0 ? (
        <Grid item xs={12}>
          <Button
            onClick={() => {
              append(defaultItem());
              onFormChange();
            }}
            className="font-Saira font-semibold italic text-[12px] border-0 p-0 bg-transparent capitalize"
            style={{ color: themeColors?.primary }}
          >
            <Add
              sx={{
                fontSize: 15,
                color: themeColors?.primary,
                marginTop: "-3px",
              }}
            />
            {t("addChecklistItem")}
          </Button>
        </Grid>
      ) : null}

      {deleteTarget ? (
        <FormDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title={t("delete") + " " + t("ChecklistItem")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("checkListItemDeleteConfirmation")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={confirmDeleteItem}
          submitButtonText="yes"
          cancelBtnText="no"
        />
      ) : null}
    </>
  );
}

export default function ChecklistModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, dirtyFields, isDirty },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      title: "",
      items: [defaultItem()],
    },
  });
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "general",
    moduleLabelKey: "Checklists",
    moduleHref: "/checklists",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });

  const types = [
    { value: "radio", label: t("radio") },
    { value: "select", label: t("dropdown") },
    { value: "number", label: t("number") },
    { value: "text", label: t("text") },
    { value: "checkbox", label: t("checkbox") },
    { value: "date", label: t("date") },
    { value: "textarea", label: t("textarea") },
  ];

  const markFormChanged = () => {
    if (params?.id) setIsBtnEnabled(true);
  };

  const mapItemToForm = (item: any) => {
    const typeValue = item?.type || "";
    return {
      id: item?.id,
      title: item?.title || "",
      type: typeValue
        ? {
            value: typeValue,
            label: typeValue === "select" ? t("dropdown") : t(typeValue),
          }
        : null,
      options: normalizeOptions(item?.options, typeValue),
      active: item?.active ?? true,
      value: item?.value ?? null,
    };
  };

  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);

  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);

  const getByID = () => {
    ChecklistProfileService.getChecklistById(+(params as any)?.id)
      .then((res: GetChecklistByIdResponse) => {
        if (res.success) {
          const data = res?.data || {};
          const title = data?.title || data?.checkListTitle || "";
          const items = Array.isArray(data?.items) ? data.items : [];
          reset({
            title,
            items: items.length ? items.map(mapItemToForm) : [defaultItem()],
          });
          captureLabel(title);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  };

  const buildPayload = (data: FormData): ChecklistWithItemsRequest => {
    const items = (data.items || []).map((item: any) => {
      const typeValue =
        typeof item?.type === "object" ? item?.type?.value : item?.type;
      const isMulti = needsOptions(typeValue);
      const payload: ChecklistWithItemsRequest["items"][number] = {
        options: isMulti
          ? (item?.options || [])
              .map((o: string) => (typeof o === "string" ? o.trim() : o))
              .filter((o: string) => !!o)
          : [],
        title: (item?.title || "").trim(),
        type: typeValue,
        multiple: isMulti,
        value: item?.value ?? null,
        active: item?.active ?? true,
      };
      if (item?.id) payload.id = item.id;
      return payload;
    });
    return {
      checkListTitle: (data.title || "").trim(),
      items,
    };
  };

  const onSubmit = (data: any) => {
    if (!data?.items?.length) {
      enqueueSnackbar(
        <AlertMessages
          text={t("ChecklistItem") + " — " + t("This field is required.")}
        />,
        { variant: "error" },
      );
      return;
    }
    const missingOption = (data.items || []).some((item: any) => {
      const typeValue =
        typeof item?.type === "object" ? item?.type?.value : item?.type;
      if (!needsOptions(typeValue)) return false;
      const options = Array.isArray(item?.options) ? item.options : [];
      return (
        options.length === 0 ||
        options.every((o: string) => !(typeof o === "string" ? o.trim() : o))
      );
    });
    if (missingOption) {
      enqueueSnackbar(
        <AlertMessages
          text={t("option") + " — " + t("This field is required.")}
        />,
        { variant: "error" },
      );
      return;
    }
    setIsBtnEnabled(false);
    const payload = buildPayload(data);

    if (params?.id) {
      ChecklistProfileService.updateChecklistWithItems(+params?.id, payload)
        .then((response: any) => {
          if (response.success) {
            captureLabel(payload.checkListTitle);
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Checklist") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            const resData = response?.data || {};
            const title =
              resData?.title ||
              resData?.checkListTitle ||
              payload.checkListTitle;
            const items = Array.isArray(resData?.items)
              ? resData.items
              : payload.items;
            reset({
              title,
              items: items.length ? items.map(mapItemToForm) : [defaultItem()],
            });
            setIsBtnEnabled(false);
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    } else {
      ChecklistProfileService.createChecklistWithItems(payload)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("Checklist") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    }
  };

  const canSave = params?.id
    ? checkPrivileges("ROLE_CHECKLIST_UPDATE")
    : checkPrivileges("ROLE_CHECKLIST_CREATE");

  return (
    <>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={canSave}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={params?.id ? t("editChecklist") : t("addChecklist")}
        icon={<LocalAtmIcon className="text-black w-[34px] h-[34px]" />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_CHECKLIST_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <Box>
          <SimpleModal
            padding={false}
            visable={true}
            title={params?.id ? "editChecklist" : "addChecklist"}
            isButtonVisable={true}
            isVisableBtn={IsBtnEnabled && canSave}
            btnclosetext="Back"
            buttonText={params?.id ? "update" : "submit"}
            attBtnNotshow={false}
            notVisableBackbtn={true}
            showPortList={handleSubmit(onSubmit)}
            formSubmit={handleSubmit(onSubmit)}
            content={
              <Grid
                container
                component="form"
                id="form"
                className="items-baseline"
                spacing={2}
                onSubmit={handleSubmit(onSubmit)}
              >
                <Grid item xs={12} md={4}>
                  <TextField
                    {...register("title", {
                      validate: {
                        required: (value) =>
                          (typeof value === "string" ? value.trim() : value) !==
                            "" || t("This field is required."),
                      },
                      onChange: () => markFormChanged(),
                    })}
                    label={t("title") + " *"}
                    placeholder={t("phChecklistTitle")}
                    title={t("title")}
                    fullWidth
                    error={!!errors.title}
                    helperText={errors.title?.message as string}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <ChecklistItemsFieldArray
                  control={control}
                  register={register}
                  errors={errors}
                  watch={watch}
                  setValue={setValue}
                  types={types}
                  themeColors={themeColors}
                  onFormChange={markFormChanged}
                  setIsBtnEnabled={setIsBtnEnabled}
                  IsBtnEnabled={IsBtnEnabled}
                  isEditMode={!!params?.id}
                />
              </Grid>
            }
          />
        </Box>
      )}
    </>
  );
}
